import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ReferralReportsService } from './referral-reports.service';
import { ReferralDailyReport, ReferralWeeklyReport } from '../../Models/ReferralReport';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type ReportMode = 'daily' | 'weekly';

@Component({
  selector: 'app-referral-reports-dialog',
  templateUrl: './referral-reports-dialog.component.html',
  styleUrls: ['./referral-reports-dialog.component.scss'],
  standalone: false,
})
export class ReferralReportsDialogComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() closed = new EventEmitter<void>();

  mode: ReportMode = 'daily';
  dayDate = '';
  wkFrom = '';
  wkTo = '';
  errorMessage = '';
  selectionLine = '';
  loading = false;
  downloading = false;
  activeTab = 0;
  dailyReport: ReferralDailyReport | null = null;
  weeklyReport: ReferralWeeklyReport | null = null;
  private reportRequest: Subscription | null = null;
  private downloadRequest: Subscription | null = null;
  private readonly cancelRequest$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  readonly todayIso = this.toIso(new Date());

  constructor(private readonly reportsService: ReferralReportsService) {}

  get tabs(): string[] {
    return ['GBCS MSA Daily Referrals'];
  }

  get reportFileName(): string {
    return this.dailyReport ? 'GBCS_MSA_Daily_Referrals_' + this.dailyReport.day + '.xlsx' : '';
  }

  get reportCount(): number {
    return this.dailyReport?.grandTotal ?? 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.resetDialog();
      } else {
        this.cancelPendingRequests();
      }
    }
  }

  ngOnDestroy(): void {
    this.cancelPendingRequests();
    this.destroy$.next();
    this.destroy$.complete();
  }

  setMode(mode: ReportMode): void {
    this.mode = mode;
    this.clearReport();
    this.validate();
  }

  setQuickDay(daysAgo: number): void {
    this.dayDate = this.toIso(this.addDays(new Date(), -daysAgo));
    this.clearReport();
    this.validate();
  }

  setQuickWeek(kind: 'this' | 'last'): void {
    const monday = this.mondayOf(new Date());
    const today = new Date();
    const from = kind === 'this' ? monday : this.addDays(monday, -7);
    const to = kind === 'this' ? today : this.addDays(from, 6);
    this.wkFrom = this.toIso(from);
    this.wkTo = this.toIso(to);
    this.clearReport();
    this.validate();
  }

  onWkFromChange(): void {
    if (this.wkFrom) {
      const proposedTo = this.toIso(this.addDays(this.parseIso(this.wkFrom), 6));
      this.wkTo = proposedTo < this.todayIso ? proposedTo : this.todayIso;
    }
    this.clearReport();
    this.validate();
  }

  onFieldChange(): void {
    this.clearReport();
    this.validate();
  }

  validate(): boolean {
    this.errorMessage = '';
    this.selectionLine = '';
    let ok = true;

    if (this.mode === 'daily') {
      if (!this.dayDate) {
        this.errorMessage = 'Choose a report date.';
        ok = false;
      } else if (this.dayDate > this.todayIso) {
        this.errorMessage = "The report date can't be in the future.";
        ok = false;
      } else {
        this.selectionLine = `GBCS MSA Daily Referrals for <strong>${this.formatLong(this.dayDate)}</strong>`;
      }
    } else {
      if (!this.wkFrom || !this.wkTo) {
        this.errorMessage = 'Choose both a From date and a To date.';
        ok = false;
      } else if (this.wkFrom > this.todayIso || this.wkTo > this.todayIso) {
        this.errorMessage = "Dates can't be in the future.";
        ok = false;
      } else if (this.wkFrom > this.wkTo) {
        this.errorMessage = 'The From date must be on or before the To date.';
        ok = false;
      } else {
        const days = this.diffDays(this.wkFrom, this.wkTo) + 1;
        if (days > 7) {
          this.errorMessage = `That's ${days} days. A weekly report covers up to 7 days.`;
          ok = false;
        } else {
          this.selectionLine = `GBCS MSA Weekly Referrals from ${this.formatLong(this.wkFrom)} to ${this.formatLong(this.wkTo)} (${days} day${days > 1 ? 's' : ''})`;
        }
      }
    }

    return ok;
  }

  generate(): void {
    if (!this.validate() || this.loading) {
      return;
    }

    this.cancelPendingRequests();

    const requestedMode = this.mode;
    const requestedDate = this.dayDate;
    const requestedFrom = this.wkFrom;
    const requestedTo = this.wkTo;
    const request$: Observable<ReferralDailyReport | ReferralWeeklyReport> = requestedMode === 'daily'
      ? this.reportsService.getDailyReport({ date: requestedDate }) as Observable<ReferralDailyReport | ReferralWeeklyReport>
      : this.reportsService.getWeeklyReport({ from: requestedFrom, to: requestedTo }) as Observable<ReferralDailyReport | ReferralWeeklyReport>;

    this.loading = true;
    this.reportRequest = request$
      .pipe(takeUntil(this.cancelRequest$), takeUntil(this.destroy$))
      .subscribe({
        next: (report) => {
          if (!this.isOpen || !this.isCurrentSelection(requestedMode, requestedDate, requestedFrom, requestedTo)) {
            this.loading = false;
            this.reportRequest = null;
            return;
          }

          if (requestedMode === 'daily') {
            const daily = report as ReferralDailyReport;
            if (daily?.day !== requestedDate || this.dayDate !== requestedDate) {
              this.loading = false;
              this.reportRequest = null;
              return;
            }
            this.dailyReport = daily;
          } else {
            this.weeklyReport = report as ReferralWeeklyReport;
          }
          this.activeTab = 0;
          this.loading = false;
          this.reportRequest = null;
        },
        error: (err: unknown) => {
          if (!this.isOpen || !this.isCurrentSelection(requestedMode, requestedDate, requestedFrom, requestedTo)) {
            this.loading = false;
            this.reportRequest = null;
            return;
          }

          this.errorMessage = this.extractErrorMessage(err);
          this.loading = false;
          this.reportRequest = null;
        },
      });
  }

  download(): void {
    if (this.downloading) {
      return;
    }

    const exportDate = this.mode === 'daily'
      ? (this.dailyReport?.day || this.dayDate)
      : this.dayDate;

    this.downloading = true;
    const request$ = this.mode === 'daily'
      ? this.reportsService.exportDailyReport({ date: exportDate })
      : this.reportsService.exportWeeklyReport({ from: this.weeklyReport?.from ?? this.wkFrom, to: this.weeklyReport?.to ?? this.wkTo });

    this.downloadRequest = request$
      .pipe(takeUntil(this.cancelRequest$), takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (this.isOpen && response?.downloadURL) {
            this.triggerDownload(response.downloadURL, this.reportFileName);
          }
          this.downloading = false;
          this.downloadRequest = null;
        },
        error: (err) => {
          if (this.isOpen) {
            this.errorMessage = this.extractErrorMessage(err) || 'Failed to download the report. Please try again.';
          }
          this.downloading = false;
          this.downloadRequest = null;
        },
      });
  }

  private triggerDownload(url: string, filename: string): void {
    const link = document.createElement('a');
    link.href = url;
    if (filename) {
      link.setAttribute('download', filename);
    }
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  close(): void {
    this.cancelPendingRequests();
    this.closed.emit();
  }

  get hasReport(): boolean {
    return this.mode === 'daily' ? !!this.dailyReport : !!this.weeklyReport;
  }

  formatShortDate(iso: string): string {
    if (!iso) return '';
    const d = this.parseIso(iso);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}-${dd}-${yy}`;
  }

  private resetDialog(): void {
    this.mode = 'daily';
    this.dayDate = this.toIso(this.addDays(new Date(), -1));
    this.setQuickWeek('last');
    this.clearReport();
    this.errorMessage = '';
    this.validate();
  }

  private clearReport(): void {
    this.cancelPendingRequests();
    this.dailyReport = null;
    this.weeklyReport = null;
    this.activeTab = 0;
  }

  private cancelPendingRequests(): void {
    this.cancelRequest$.next();
    this.reportRequest?.unsubscribe();
    this.downloadRequest?.unsubscribe();
    this.reportRequest = null;
    this.downloadRequest = null;
    this.loading = false;
    this.downloading = false;
  }

  private isCurrentSelection(mode: ReportMode, date: string, from: string, to: string): boolean {
    return this.mode === mode && (mode === 'daily'
      ? this.dayDate === date
      : this.wkFrom === from && this.wkTo === to);
  }

  private extractErrorMessage(err: unknown): string {
    const httpError = err as { error?: unknown };
    if (typeof httpError?.error === 'string' && httpError.error) {
      return httpError.error;
    }
    return 'Failed to generate the report. Please try again.';
  }

  private formatLong(iso: string): string {
    return this.parseIso(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  }

  private toIso(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private parseIso(s: string): Date {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  private addDays(d: Date, n: number): Date {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  }

  private diffDays(a: string, b: string): number {
    return Math.round((this.parseIso(b).getTime() - this.parseIso(a).getTime()) / 86400000);
  }

  private mondayOf(d: Date): Date {
    const day = (d.getDay() + 6) % 7;
    return this.addDays(d, -day);
  }
}
